import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Eye,
  RefreshCw,
  MoreHorizontal,
  XCircle,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn, formatDateTime, formatDuration } from '@/lib/utils';
import { useAuth } from '@/hooks';
import { hasPermission } from '@/types';
import { ForceFailMigrationDialog } from '@/components/migrations/ForceFailMigrationDialog';
import type { MigrationSummary, MigrationStatus } from '@/types';

interface HistoryTableProps {
  migrations: MigrationSummary[];
  sortBy: string;
  sortDesc: boolean;
  onSort: (column: string) => void;
  onRetry?: ((id: string) => void) | undefined;
  onForceFail?: ((id: string) => void) | undefined;
  isForcingId?: string | undefined;
}

const statusVariant: Record<MigrationStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  InProgress: 'default',
  Succeeded: 'outline',
  Partial: 'secondary',
  Failed: 'destructive',
};

const statusLabel: Record<MigrationStatus, string> = {
  InProgress: 'In Progress',
  Succeeded: 'Succeeded',
  Partial: 'Partial',
  Failed: 'Failed',
};

interface SortableHeaderProps {
  column: string;
  label: string;
  currentSort: string;
  sortDesc: boolean;
  onSort: (column: string) => void;
}

function SortableHeader({ column, label, currentSort, sortDesc, onSort }: SortableHeaderProps) {
  const isActive = currentSort === column;

  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 data-[state=open]:bg-accent"
      onClick={() => onSort(column)}
    >
      {label}
      {isActive ? (
        sortDesc ? (
          <ChevronDown className="ml-2 h-4 w-4" />
        ) : (
          <ChevronUp className="ml-2 h-4 w-4" />
        )
      ) : (
        <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
      )}
    </Button>
  );
}

export function HistoryTable({
  migrations,
  sortBy,
  sortDesc,
  onSort,
  onRetry,
  onForceFail,
  isForcingId,
}: HistoryTableProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.roles?.some((role) => hasPermission(role, 'admin')) ?? false;
  const [forceFailTarget, setForceFailTarget] = useState<MigrationSummary | null>(null);

  const handleRowClick = (id: string) => {
    navigate({ to: '/migrations/$migrationId', params: { migrationId: id } });
  };

  return (
    <>
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <SortableHeader
                column="organizationName"
                label="Organization"
                currentSort={sortBy}
                sortDesc={sortDesc}
                onSort={onSort}
              />
            </TableHead>
            <TableHead>
              <SortableHeader
                column="status"
                label="Status"
                currentSort={sortBy}
                sortDesc={sortDesc}
                onSort={onSort}
              />
            </TableHead>
            <TableHead>Started By</TableHead>
            <TableHead>
              <SortableHeader
                column="startedAt"
                label="Started"
                currentSort={sortBy}
                sortDesc={sortDesc}
                onSort={onSort}
              />
            </TableHead>
            <TableHead>Duration</TableHead>
            <TableHead>Modules</TableHead>
            <TableHead className="w-[70px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {migrations.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                No migrations found.
              </TableCell>
            </TableRow>
          ) : (
            migrations.map((migration) => (
              <TableRow
                key={migration.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleRowClick(migration.id)}
              >
                <TableCell className="font-medium">
                  <div>
                    {migration.organizationName}
                    <p className="text-xs text-muted-foreground">
                      {migration.organizationType === 'New' ? 'New Org' : 'Existing Org'}
                      {migration.bridgeEntityName && (
                        <span> • Bridge: {migration.bridgeEntityName}</span>
                      )}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={statusVariant[migration.status]}>
                    {statusLabel[migration.status]}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {migration.startedByUserName}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatDateTime(migration.startedAt)}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatDuration(migration.startedAt, migration.completedAt)}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <span
                      className={cn(
                        'text-sm',
                        migration.failedModules > 0 && 'text-destructive'
                      )}
                    >
                      {migration.completedModules}/{migration.totalModules}
                    </span>
                    {migration.failedModules > 0 && (
                      <span className="text-xs text-destructive">
                        ({migration.failedModules} failed)
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Open menu</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRowClick(migration.id);
                        }}
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        View Details
                      </DropdownMenuItem>
                      {migration.failedModules > 0 && onRetry && (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            onRetry(migration.id);
                          }}
                        >
                          <RefreshCw className="mr-2 h-4 w-4" />
                          Retry Failed
                        </DropdownMenuItem>
                      )}
                      {isAdmin && migration.status === 'InProgress' && onForceFail && (
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onSelect={(e) => {
                            e.preventDefault();
                            setForceFailTarget(migration);
                          }}
                        >
                          <XCircle className="mr-2 h-4 w-4" />
                          Force Fail
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>

    <ForceFailMigrationDialog
      open={forceFailTarget !== null}
      onOpenChange={(open) => {
        if (!open) setForceFailTarget(null);
      }}
      migrationName={forceFailTarget?.organizationName ?? ''}
      onConfirm={() => {
        if (forceFailTarget) {
          onForceFail?.(forceFailTarget.id);
          setForceFailTarget(null);
        }
      }}
      isForcing={isForcingId === forceFailTarget?.id}
    />
    </>
  );
}
